import { ChevronDown, KeyRound, Menu } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import quickcommLogo from "../../assets/logo.png";
import userPic from "../../assets/UserPic.png";
import { useAuthStore } from "../../features/auth/store/authStore";
import { PendingHandoffsBell } from "./PendingHandoffsBell";
import { PropertySelect } from "./PropertySelect";
import { usePropertyStore } from "../../store/propertyStore";
import { useUiStore } from "../../store/uiStore";
import {
  usePlatformFilterStore,
  useFilteredOrgs,
  useFilteredProperties,
  useOrgTypes,
} from "../../store/platformFilterStore";

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  MANAGER: "Manager",
  STAFF: "Staff",
};

interface BrandLockupProps {
  className: string;
}

// Reads "<org> in collaboration with QuickComm". No org logo, or one that
// fails to load, collapses to QuickComm alone. Logos keep their natural width
// and only shrink when the slot is too narrow — an even split pads whichever
// logo is squarer.
function BrandLockup({ className }: BrandLockupProps): JSX.Element {
  const orgLogo = useAuthStore((s) => s.user?.organization_logo_url);
  const orgName = useAuthStore((s) => s.user?.organization_name);
  const [orgLogoBroken, setOrgLogoBroken] = useState(false);
  const paired = !!orgLogo && !orgLogoBroken;
  const imgCls = "h-full w-auto min-w-0 object-contain";

  return (
    <div className={`flex min-w-0 items-center justify-center gap-2 ${className}`}>
      {paired && (
        <>
          <img
            src={orgLogo}
            alt={orgName || "Organization"}
            className={imgCls}
            onError={() => setOrgLogoBroken(true)}
          />
          <span aria-hidden="true" className="flex-shrink-0 text-[14px] text-gray-400">×</span>
        </>
      )}
      <img src={quickcommLogo} alt="QuickComm" className={imgCls} />
    </div>
  );
}

// ── Platform admin cascading filter bar ──
function PlatformFilterBar({ compact = false }: { compact?: boolean }): JSX.Element {
  const { selectedOrgType, selectedOrgId, selectedPropertyId, selectOrgType, selectOrg, selectProperty, loading } =
    usePlatformFilterStore()
  const orgTypes = useOrgTypes()
  const filteredOrgs = useFilteredOrgs()
  const filteredProperties = useFilteredProperties()

  const selectCls = `h-9 appearance-none truncate rounded-lg border border-gray-200 bg-white px-3 pr-8 text-[13px] text-gray-700 outline-none focus:border-brand-green disabled:opacity-50 ${compact ? 'w-full' : 'w-[160px] lg:w-[190px]'}`

  return (
    <div className={`flex ${compact ? 'flex-col gap-2' : 'items-center gap-2'}`}>
      {/* Org Type */}
      <div className="relative">
        <select
          value={selectedOrgType ?? ""}
          onChange={(e) => selectOrgType(e.target.value || null)}
          disabled={loading}
          className={selectCls}
        >
          <option value="">All Types</option>
          {orgTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>

      {/* Organization */}
      <div className="relative">
        <select
          value={selectedOrgId ?? ""}
          onChange={(e) => selectOrg(e.target.value ? Number(e.target.value) : null)}
          disabled={loading}
          className={selectCls}
        >
          <option value="">All Organisations</option>
          {filteredOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>

      {/* Property */}
      <div className="relative">
        <select
          value={selectedPropertyId ?? ""}
          onChange={(e) => selectProperty(e.target.value ? Number(e.target.value) : null)}
          disabled={loading}
          className={selectCls}
        >
          <option value="">All Properties</option>
          {filteredProperties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>
    </div>
  )
}

export function TopBar(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = !!user?.is_platform_admin;
  const fetchProperties = usePropertyStore((s) => s.fetchProperties);
  const toggleMobileSidebar = useUiStore((s) => s.toggleMobileSidebar);
  const selectorInFlight = useUiStore((s) => s.propertySelectorInFlight);
  // Laid out but invisible, never display:none — the landing page measures this
  // to know where to fly to.
  const selectorCls = selectorInFlight ? "opacity-0 pointer-events-none" : "opacity-100";
  const fetchPlatformAll = usePlatformFilterStore((s) => s.fetchAll);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);
  const desktopUserMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    function handleClickOutside(event: MouseEvent): void {
      const target = event.target as Node;
      const clickedMobileMenu = mobileUserMenuRef.current?.contains(target);
      const clickedDesktopMenu = desktopUserMenuRef.current?.contains(target);
      if (!clickedMobileMenu && !clickedDesktopMenu) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (isPlatformAdmin) {
      fetchPlatformAll();
    } else if (user?.property_ids) {
      fetchProperties(user.property_ids);
    }
  }, [isPlatformAdmin, user?.property_ids, fetchProperties, fetchPlatformAll]);

  const displayName = user?.name || "User";
  const displayRole = user?.role ? (ROLE_LABELS[user.role] ?? user.role) : "";

  return (
    <header className="border-b border-gray-200 bg-white">
      <div className="flex h-14 items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-4 md:hidden">
        <button
          type="button"
          aria-label="Open navigation menu"
          onClick={toggleMobileSidebar}
          className="-ml-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
        >
          <Menu size={20} />
        </button>
        <BrandLockup className="h-6 max-w-[150px] flex-shrink-0 sm:h-8 sm:max-w-[220px]" />

        {isPlatformAdmin ? (
          <div className="min-w-0 flex-1 overflow-x-auto">
            <PlatformFilterBar compact={false} />
          </div>
        ) : (
        <div className={`relative min-w-0 flex-1 ${selectorCls}`}>
          <PropertySelect
            className="h-9 w-full appearance-none truncate rounded-lg border border-gray-200 bg-white px-3 pr-9 text-[13px] text-gray-700 outline-none focus:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:pr-10 sm:text-[14px]"
            chevronCls="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 sm:right-3"
          />
        </div>
        )}

        <div className="ml-auto flex flex-shrink-0 items-center gap-0.5 sm:gap-1">
          <PendingHandoffsBell />

          <div ref={mobileUserMenuRef} className="relative flex items-center gap-2 sm:gap-3">
            <div className="hidden flex-col items-end leading-tight lg:flex">
              <span className="max-w-[140px] truncate text-[14px] font-bold text-gray-900 xl:max-w-[200px]">
                {displayName}
              </span>
              <span className="text-[12px] text-gray-500">{displayRole}</span>
            </div>
            <button
              type="button"
              aria-label="User menu"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              className="rounded-full outline-none ring-brand-green transition-opacity hover:opacity-90 focus-visible:ring-2"
            >
              <img
                src={userPic}
                alt={displayName}
                className="h-8 w-8 rounded-full object-cover sm:h-9 sm:w-9"
              />
            </button>

            {isUserMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
              >
                <div className="border-b border-gray-100 px-4 py-2 lg:hidden">
                  <p className="truncate text-[13px] font-semibold text-gray-900">{displayName}</p>
                  <p className="text-[12px] text-gray-500">{displayRole}</p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate("/change-password");
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <KeyRound size={16} className="text-gray-500" />
                  Change password
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="hidden h-16 md:grid md:grid-cols-[15rem_minmax(0,1fr)]">
        <div className="flex items-center border-r border-gray-200 px-4">
          <BrandLockup className="h-8 w-full" />
        </div>

        <div className="flex min-w-0 items-center px-4 lg:px-6">
          <div className="flex w-full min-w-0 items-center">
            {isPlatformAdmin ? (
              <PlatformFilterBar />
            ) : (
              <div className={`relative w-[220px] lg:w-[260px] ${selectorCls}`}>
                <PropertySelect
                  className="h-9 w-full appearance-none truncate rounded-lg border border-gray-200 bg-white px-4 pr-10 text-[14px] text-gray-700 outline-none focus:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                  chevronCls="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>
            )}

            <div className="ml-auto flex items-center gap-2">
              <PendingHandoffsBell />

              <div ref={desktopUserMenuRef} className="relative flex items-center gap-3">
                <div className="hidden flex-col items-end leading-tight lg:flex">
                  <span className="max-w-[140px] truncate text-[14px] font-bold text-gray-900 xl:max-w-[200px]">
                    {displayName}
                  </span>
                  <span className="text-[12px] text-gray-500">{displayRole}</span>
                </div>
                <button
                  type="button"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  className="rounded-full outline-none ring-brand-green transition-opacity hover:opacity-90 focus-visible:ring-2"
                >
                  <img
                    src={userPic}
                    alt={displayName}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                </button>

                {isUserMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                  >
                    <div className="border-b border-gray-100 px-4 py-2 lg:hidden">
                      <p className="truncate text-[13px] font-semibold text-gray-900">{displayName}</p>
                      <p className="text-[12px] text-gray-500">{displayRole}</p>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        navigate("/change-password");
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <KeyRound size={16} className="text-gray-500" />
                      Change password
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </header>
  );
}
